// Resolve the API base URL based on the browser's current hostname.
//
// - On platform hosts (xale.in / app.xale.in / localhost / *.xale.local)
//   we use the static VITE_BASE_URL — preserves existing dev/staging
//   behavior and lets us point at any environment via env.
// - On a tenant's custom domain (crm.acme.com), we call /api/v1 on the
//   SAME origin. Cloudflare for SaaS routes /api/* and /socket.io/*
//   from the custom hostname through to our origin. This keeps tenant
//   traffic same-origin so cookies, CORS preflight, and the address bar
//   all reflect the tenant's brand.
//
// Stays a pure function of window.location so React Query / axios singletons
// pick the right URL at module-load time without any extra wiring.

const PLATFORM_HOST_SUFFIXES = [".xale.in", ".xale.local"];
const PLATFORM_HOST_EXACT = new Set([
  "xale.in",
  "localhost",
  "127.0.0.1",
  "0.0.0.0",
]);

function isPlatformHost(host: string): boolean {
  if (PLATFORM_HOST_EXACT.has(host)) return true;
  return PLATFORM_HOST_SUFFIXES.some((s) => host.endsWith(s));
}

export function getApiBaseUrl(): string {
  const envBase = (import.meta as any).env?.VITE_BASE_URL as string | undefined;
  const fallback = envBase || "https://api.xale.in/api/v1";

  if (typeof window === "undefined") return fallback;

  const host = window.location.hostname.toLowerCase();
  if (isPlatformHost(host)) return fallback;

  // Custom tenant domain — same origin, /api/v1 path.
  return `${window.location.origin}/api/v1`;
}

export function getApiOrigin(): string {
  try {
    return new URL(getApiBaseUrl()).origin;
  } catch {
    return window?.location?.origin ?? "https://api.xale.in";
  }
}
